import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Grid, GridSize, Tooltip, Typography, useTheme } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useGeneralTasksStore } from './hooks/useGeneralTasksStore';
import { Button, CrescentSpinner, LinkColumn, Paper, PDFPreview, StatusChip, StatusType } from '@roc/ui';
import { Alert } from '@material-ui/lab';
import { GeneralTasksToolbar } from './components/generalTasksToolbar';
import { Add, ArrowUpwardRounded, Remove, UnfoldLess } from '@material-ui/icons';
import { useBaseStore } from '@roc/feature-app-core';
import { DocumentForms, useDocumentStore } from '@roc/feature-documents';
import clsx from 'clsx';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { formatDate, isNil, LoanProcess } from '@roc/feature-utils';
import { StyledBadge } from '@roc/feature-loans';
import { TodoActions, TodoTitleLink } from 'libs/feature-loan-details/src/loanDetails/components/cards/todoActions';
import { DocumentTitleCell } from './components/taskList/documentTitleCell';
import { GeneralTasksTab } from './types/generalTasksTypes';
import { EditTaskDialog } from './components/editTask/editTaskDialog';
import { AddLoanTaskDialog } from './components/addLoanTask/addLoanTaskDialog';
import { CustomTaskDialog } from './components/customTask/customTaskDialog';
import { DocumentIdCell } from './components/taskList/documentIdCell';
import { LOAN_RELATED_DOCUMENTS, LOAN_RELATED_DOCUMENTS_ID, TaskStatus } from '@roc/feature-general-tasks';

// Same look as the lender To-Dos accordions: each loan is a rounded bordered card
const CARD_BORDER = '1px solid #EBEBEB';
const CARD_RADIUS = 16;
const OPEN_ROW_BACKGROUND = '#F0F5FF';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2, 3),
    borderRadius: 24,
    border: 'none',
    boxShadow: `${theme.shadows[9]} !important`,
  },
  // Grid "spacing" adds negative side margins, which made the scroll box below always 8px too wide
  noGutters: {
    margin: 0,
    width: '100%',
  },
  loanHeaderRow: {
    padding: theme.spacing(0, 2, 0.5, 6),
  },
  taskHeaderRow: {
    borderTop: CARD_BORDER,
    borderBottom: CARD_BORDER,
    backgroundColor: '#fff',
  },
  headerText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1B1D34',
    lineHeight: '40px',
  },
  loanItem: {
    paddingBottom: theme.spacing(1),
  },
  loanCard: {
    margin: '0 !important',
    border: CARD_BORDER,
    borderRadius: `${CARD_RADIUS}px !important`,
    overflow: 'hidden',
    '& > .MuiAccordionSummary-root': {
      minHeight: '52px !important',
      padding: theme.spacing(0, 2, 0, 1.5),
    },
    '& > .MuiAccordionSummary-root.Mui-expanded': {
      backgroundColor: OPEN_ROW_BACKGROUND,
    },
    '& .MuiAccordionSummary-content': {
      minWidth: 0,
      overflow: 'hidden',
    },
    '& .MuiAccordionSummary-expandIcon': {
      flexShrink: 0,
    },
  },
  // no underlined links in this grid
  noUnderline: {
    // some cells underline the wrapper around the link, so clear it on everything
    '& *': {
      textDecoration: 'none !important',
    },
  },
  hideExpand: {
    '& .MuiAccordionSummary-expandIcon ': {
      display: 'none',
    }
  },
  noRecordsPadding: {
    paddingLeft: `${theme.spacing(2)}px !important`
  },
  rowPadding: {
    paddingLeft: theme.spacing(5)
  },
  escalatedIcon: {
    color: 'rgb(217, 68, 82)',
  },
  escalatedTooltip: {
    border: '2px solid rgb(217, 68, 82)',
    borderRadius: '14px',
  },
  escalatedWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
}))

const HeaderCell = ({ label }) => {
  const classes = useStyles();
  return <Typography variant="body1" className={classes.headerText}>{label}</Typography>
}

const getLoanRowFlexStyle = (loanRowSpecs, loanRowSpec) => {
  const totalSize = loanRowSpecs?.reduce((sum, spec) => sum + (spec?.size ?? 0), 0) || 1;
  const minWidthPercent = ((loanRowSpec?.size ?? 0) / totalSize) * 100;
  return {
    flex: `${loanRowSpec?.size ?? 1} 1 0`,
    minWidth: `${minWidthPercent}%`,
  };
};

const LoanHeaderRow = ({ loanRowSpecs }) => {
  const classes = useStyles();
  return (
    <Grid item xs={12}>
      <Grid container direction='row' spacing={1} wrap="nowrap" className={clsx(classes.noGutters, classes.loanHeaderRow)}>
        {loanRowSpecs?.map(loanRowSpec => (
          <Grid
            item
            style={{ ...getLoanRowFlexStyle(loanRowSpecs, loanRowSpec), ...loanRowSpec?.headerStyle }}
          >
            <HeaderCell label={loanRowSpec.label} />
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
}

const LoanRow = ({ loanRowSpecs, row }) => {
  const classes = useStyles();
  return (
  <Grid container direction='row' spacing={1} wrap="nowrap" alignItems="center" className={classes.noGutters}>
    {loanRowSpecs?.map(loanRowSpec => (
      <Grid
        item
        style={{ ...getLoanRowFlexStyle(loanRowSpecs, loanRowSpec), ...loanRowSpec?.rowStyle }}
      >
        {loanRowSpec?.renderValue ?
          loanRowSpec?.renderValue(row)
          :
          <Cell value={row[loanRowSpec?.field]} />
        }
      </Grid>
    ))}
  </Grid>
  );
};

const Cell = ({ value }) => {
  return <Typography variant="body1">{value}</Typography>
}

const LoanTasksRow = ({ taskRowSpecs, row, expandAll, collapseAll }) => {
  const classes = useStyles();
  return (
  <Grid container direction='row' className={classes.noGutters}>
    <TaskHeaderRow taskRowSpecs={taskRowSpecs} />
    <TaskRows
      taskRowSpecs={taskRowSpecs}
      rows={row.loanTasks}
      expandAll={expandAll}
      collapseAll={collapseAll}
    />
  </Grid>
  );
};

const useAccordionStyles = makeStyles((theme: Theme) => ({
  accordion: {
    '&.Mui-expanded': {
      margin: 0,
    },
    '&::before': {
      display: 'none',
    },
    '&.MuiAccordion-rounded:last-child': {
      borderRadius: '0px',
    },
    borderBottom: CARD_BORDER,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover > .MuiAccordionSummary-root': {
      backgroundColor: '#F7F8FC',
    },
  },
  category: {
    borderRadius: '0px',
    flexDirection: 'row-reverse',
    flex: '1 0 0',
    minHeight: '35px !important',
    cursor: 'pointer',
    userSelect: 'text',
    '& .MuiAccordionSummary-content': {
      margin: '0',
    },
    '& .MuiAccordionSummary-root': {
      minHeight: '35px !important',
    },
    '&.Mui-expanded': {
      borderRadius: '0px',
    },
    '& .MuiAccordionSummary-expandIcon': {
      padding: 6,
      marginRight: theme.spacing(0.5),
      color: '#5E69A6',
      '& svg': {
        fontSize: 20,
      },
    },
    padding: '0',
  },
}));

export const AccordionRow = observer(({ isOpen = true, expandAll = undefined, collapseAll = undefined, className = undefined, children }) => {
  const classes = useAccordionStyles();

  const [open, setOpen] = useState(isOpen);

  useEffect(() => {
    if (expandAll) {
      setOpen(true);
    }
  }, [expandAll]);

  useEffect(() => {
    if (collapseAll) {
      setOpen(false);
    }
  }, [collapseAll]);

  return (
    <Accordion elevation={0} expanded={open} className={clsx(classes.accordion, className)} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary
        expandIcon={children.length < 1 ? undefined : open ? <Remove /> : <Add />}
        className={classes.category}
        onClick={() => setOpen(prev => {
          if (children.length > 1) {
            return !prev
          }
          return false;
        })}
      >
        {children && children[0] ?
          children[0]
          :
          children
        }
      </AccordionSummary>
      <AccordionDetails style={{ padding: '0', display: 'inline' }}>
        {children && children[1] &&
          children[1]
        }
      </AccordionDetails>
    </Accordion>
  );
});

const TaskHeaderRow = ({ taskRowSpecs }) => {
  const classes = useStyles();
  return (
    <Grid item xs={12}>
      <Grid container direction='row' className={classes.taskHeaderRow}>
        <Grid item xs={12} className={classes.rowPadding}>
          <Grid container direction='row' spacing={1} wrap="nowrap" className={classes.noGutters}>
            {taskRowSpecs?.map(taskRowSpec => (
              <Grid item style={{ ...getLoanRowFlexStyle(taskRowSpecs, taskRowSpec), ...taskRowSpec?.headerStyle }}>
                <HeaderCell label={taskRowSpec.label} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const TaskRow = ({ taskRowSpecs, row }) => {
  const classes = useStyles();
  return (
  <Grid container direction='row' spacing={1} wrap="nowrap" alignItems="center" className={classes.noGutters}>
    {taskRowSpecs?.map(taskRowSpec => (
      <Grid item style={{ ...getLoanRowFlexStyle(taskRowSpecs, taskRowSpec), ...taskRowSpec?.rowStyle }}>
        {taskRowSpec?.renderValue ?
          taskRowSpec?.renderValue(row)
          :
          <Cell value={row[taskRowSpec?.field]} />
        }
      </Grid>
    ))}
  </Grid>
  );
};

const TaskRows = ({ taskRowSpecs, rows, expandAll, collapseAll }) => {
  const classes = useStyles();
  return (
    <>
      {rows.map(row => {
        const loanTaskRow = {
          ...row?.document,
          ...row,
        }
        return (
          <Grid item xs={12}>
            <Grid container direction='row'>
              <Grid item xs={12} className={classes.rowPadding}>
                <AccordionRow
                  isOpen={false}
                  expandAll={expandAll}
                  collapseAll={collapseAll}
                  className={classes.hideExpand}
                >
                  <TaskRow taskRowSpecs={taskRowSpecs} row={loanTaskRow} />
                </AccordionRow>
              </Grid>
            </Grid>
          </Grid>
        )
      })
      }
    </>
  )
}

const LoanRows = ({ loanRowSpecs, taskRowSpecs, loanRows, expandAll, collapseAll, loading = false }) => {
  const classes = useStyles();

  return (
    <>
      {loanRows?.length > 0 ?
        <>
          {loanRows?.map(loanRow => {
            return (
              <Grid item xs={12} className={classes.loanItem}>
                <AccordionRow
                  isOpen={false}
                  expandAll={expandAll}
                  collapseAll={collapseAll}
                  className={classes.loanCard}
                >
                  <LoanRow loanRowSpecs={loanRowSpecs} row={loanRow} />
                  <LoanTasksRow
                    taskRowSpecs={taskRowSpecs}
                    row={loanRow}
                    expandAll={expandAll}
                    collapseAll={collapseAll}
                  />
                </AccordionRow>
              </Grid>
            )
          })}
        </>
        :
        loading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" py={4}>
              <CrescentSpinner size={32} />
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Alert severity="info">No tasks match the current filters.</Alert>
          </Grid>
        )
      }
    </>
  )
}

export const MyLoanGeneralTasksGrouped = observer(({ teamScopeOnly = false }) => {
  const classes = useStyles();
  const formatClosingDate = (value) => (value ? formatDate(value, 'MM/dd/yyyy') : '');

  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore, loanId, loanTasksRows } = generalTasksStore;
  const [openLineItemChat, setOpenLineItemChat] = useState(false);

  const { globalStore } = useBaseStore();
  const { documentStore } = useDocumentStore();
  const { currentDocument } = documentStore;
  const { loanRoutesConfig } = useLoanRoutes();
  const theme = useTheme();

  const [expandAll, setExpandAll] = useState(0);
  const [collapseAll, setCollapseAll] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    generalTasksStore.resetAndFetchOptions();
    generalTasksStore.setTab(GeneralTasksTab.MY_LOAN_TASKS_GROUPED);
    generalTasksStore.fetchMyLoanTasksGridGrouped();
    generalTasksStore.setTeamScope(false);
  }, []);

  const loanRowSpecs = [
    {
      label: 'Loan ID',
      size: 1,
      field: 'loanId',
      renderValue: (row) => (
        <StyledBadge badgeContent={row?.totalUnreadMessageAccount}>
          <LinkColumn
            value={row?.loanId}
            url={'#'}
            onClick={e => {
              open(loanRoutesConfig.loans(row?.loanId).children.dashboard.url);
              e.preventDefault();
            }}
            textUnderline={true}
          />
        </StyledBadge>
      ),
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Deal Name',
      size: 3,
      field: 'dealName',
      renderValue: (row) => (
        <LinkColumn
          value={row?.dealName}
          url={'#'}
          onClick={e => {
            open(loanRoutesConfig.loans(row?.loanId).children.dashboard.url);
            e.preventDefault();
          }}
          textUnderline={true}
        />
      ), headerStyle: undefined,
      rowStyle: { whiteSpace: 'nowrap' },
    },
    {
      label: 'Loan Status',
      size: 2,
      field: 'status',
      renderValue: (row) => (
        <Grid container direction='row'>
          <Grid item>
            <StatusChip label={row?.status} size="small" statusType={StatusType.LOAN} />
          </Grid>
        </Grid>
      ),
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Tasks',
      size: 2,
      field: '',
      renderValue: (row) => (
        <Grid container direction='row' spacing={1} >
          <Grid item>
            <Chip
              size="small"
              variant='outlined'
              label={row?.veryCriticalAndCriticalPriorityCount}
              style={{
                borderColor: '#C62828',
                color: '#C62828',
                fontWeight: 700,
              }}
            />
          </Grid>
          <Grid item>
            <Chip
              size="small"
              variant='outlined'
              label={row?.highAndMediumPriorityCount}
              style={{
                borderColor: '#E65100',
                color: '#E65100',
                fontWeight: 700,
              }}
            />
          </Grid>
          <Grid item>
            <Chip
              size="small"
              variant='outlined'
              label={row?.lowPriorityCount}
              style={{
                borderColor: '#1B5E20',
                color: '#1B5E20',
                fontWeight: 700,
              }}
            />
          </Grid>
        </Grid>
      ),
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Requested Closing Date',
      size: 2,
      field: 'requestedClosingDate',
      renderValue: (row) => (
        <Grid container direction='row' spacing={1} justifyContent='center'>
          <Cell value={formatClosingDate(row?.requestedClosingDate)} />
        </Grid>
      ),
      headerStyle: { minWidth: '130px', textAlign: 'center' },
      rowStyle: { minWidth: '130px' },
    },
    {
      label: 'Expected Closing Date',
      size: 2,
      field: 'expectedClosingDate',
      renderValue: (row) => (
        <Grid container direction='row' spacing={1} justifyContent='center'>
          <Cell value={formatClosingDate(row?.expectedClosingDate)} />
        </Grid>
      ),
      headerStyle: { minWidth: '130px', textAlign: 'center' },
      rowStyle: { minWidth: '130px' },
    },
  ];

  const taskRowSpecs = [
    {
      label: 'Task ID',
      size: 1,
      field: 'taskId',
      renderValue: (row) => (
        <DocumentIdCell row={row} />
      ),
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Title',
      size: 4,
      field: 'title',
      renderValue: (row) => (
        <DocumentTitleCell row={row} />
      ),
      headerStyle: undefined,
      rowStyle: { whiteSpace: 'nowrap' },
    },
    {
      label: 'Status',
      size: 2,
      field: 'status',
      renderValue: (row) => {
        return (
          <StatusChip
            statusType={StatusType.GENERAL_TASK_STATUS}
            label={TaskStatus[row?.status]}
            size="small"
          />
        )
      },
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Assignee',
      size: 2,
      field: 'assigneeNames',
      renderValue: (row) => {
        const taskAssignee: string = row?.document?.taskAssignee;
        return (
          <Cell value={taskAssignee} />
        )
      },
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Escalated',
      size: 1,
      field: 'escalated',
      renderValue: (row) => (
        <>
          {!!row && !isNil(row?.escalated) ? (
            <Box className={classes.escalatedWrapper}>
              <Tooltip title="Escalated" className={classes.escalatedTooltip}>
                <ArrowUpwardRounded className={classes.escalatedIcon} />
              </Tooltip>
            </Box>
          ) : null}
        </>
      ),
      headerStyle: { textAlign: 'center' },
      rowStyle: { display: 'flex', justifyContent: 'center' },
    },
    {
      label: 'Priority',
      size: 1,
      field: 'priority',
      renderValue: (row) => (
        <StatusChip
          statusType={StatusType.PRIORITY}
          label={row.priority}
          size="small"
        />
      ),
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Time Elapsed',
      size: 2,
      field: 'timeElapsed',
      renderValue: (row) => {
        const timeElapsed: number = row?.timeElapsed;
        let displayTimeElapsed = '';
        if (timeElapsed) {
          const days = Math.trunc(timeElapsed / 86400);
          const hours = Math.trunc((timeElapsed % 86400) / 3600);
          displayTimeElapsed = (days > 0 ? `${days}D ` : '') + `${hours}H`;
        }
        return (
          <Cell value={displayTimeElapsed} />
        )
      },
      headerStyle: undefined,
      rowStyle: undefined,
    },
    {
      label: 'Actions',
      size: 1,
      field: '',
      renderValue: (row) => {
        return (
          <TodoActions
            document={documentStore.formatDocument(
              { ...row.document, escalated: row.escalated },
              LoanProcess.UNDERWRITING
            )}
            loanProcess={LoanProcess.UNDERWRITING}
            onClose={onClose}
            fetchCallback={() => generalTasksStore.fetchMyLoanTasksGridGrouped()}
          />
        )
      },
      headerStyle: { textAlign: 'center' },
      rowStyle: { display: 'flex', justifyContent: 'center' },
    },
  ];

  const init = async row => {
    const loanId = row.loanId;
    await documentStore.loanStore.fetchLoanDetails(loanId);
    generalTasksStore.setLoanId(loanId);
  };

  const onClose = () => {
    generalTasksStore.setLoanId(null);
    generalTasksStore.applyFilters();
  };

  useEffect(() => {
    if (currentDocument) {
      init(currentDocument);
    }
  }, [currentDocument]);

  return (
    <Box px={0.25} pt={1} pb={2} width="100%" style={{ flex: 1, flexDirection: "column", display: "flex" }}>
      <Box display="flex" flexDirection="column" height="100%" flex="1">
        <Box pb={2}>
          <Paper className={classes.paper}>
            <GeneralTasksToolbar teamScopeOnly={teamScopeOnly} />
          </Paper>
        </Box>
        <Box flex={1} display="flex" flexDirection="column">
          <Paper
            className={classes.paper}
            titleComponent={
              <Box display="flex" pt={2} pb={2}>
                <Grid container justifyContent="flex-end" spacing={1}>
                  {/* <Grid item>
                    <Button
                      testId="expandAll"
                      onClick={() => {
                        setExpandAll(x => x + 1);
                      }}
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={<UnfoldMore />}
                    >
                      Expand All
                    </Button>
                  </Grid> */}
                  <Grid item>
                    <Button
                      testId="collapseAll"
                      onClick={() => {
                        setCollapseAll(x => x + 1);
                      }}
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={<UnfoldLess />}
                    >
                      Collapse All
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      testId="add-task"
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => setAddDialogOpen(true)}
                    >
                      <Add />
                      New Task
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            }>
            <Box style={{ overflowX: 'auto', width: '100%' }} className={classes.noUnderline}>
              <Box style={{ minWidth: 900 }}>
                <Grid container direction='row' justifyContent='flex-end' className={classes.noGutters}>
                  <LoanHeaderRow loanRowSpecs={loanRowSpecs} />
                  <LoanRows
                    loanRowSpecs={loanRowSpecs}
                    taskRowSpecs={taskRowSpecs}
                    loanRows={loanTasksRows}
                    loading={generalTasksStore.loanTasksRowsLoading || !generalTasksStore.loanTasksRowsLoaded}
                    expandAll={expandAll}
                    collapseAll={collapseAll}
                  />
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
      {loanId && globalStore.userFeatures && (
        <>
          <DocumentForms
            loanId={loanId?.toString()}
            onClose={onClose}
          />
          <PDFPreview
            open={documentStore.documentPreview != null}
            title={documentStore.documentPreview?.title}
            data={documentStore.documentPreview?.data}
            dataV2={documentStore.documentPreview?.data}
            headers={documentStore.documentPreview?.headers}
            onClose={() => {
              onClose();
              documentStore.closeDocumentPreview();
            }}
          />
        </>
      )}
      <EditTaskDialog />
      <AddLoanTaskDialog
        open={addDialogOpen}
        loanId={loanId}
        handleClose={() => setAddDialogOpen(false)}
        sectionName={globalStore.isInternalPortal ? null : LOAN_RELATED_DOCUMENTS}
        sectionId={globalStore.isInternalPortal ? null : LOAN_RELATED_DOCUMENTS_ID}
        options={editTaskStore.loanTaskSectionNames.map(category => ({ value: category.taskCategoryId, label: category.displayName }))}
      />
    </Box>
  );
});
